import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, BadgeCheck, Shield, Store } from 'lucide-react';

const VerificationBadge = ({ verification }) => {
  if (verification === 'trusted') {
    return (
      <div className="flex items-center gap-1 text-xs text-amber-600" title="Trusted Vendor">
        <Shield className="h-3.5 w-3.5 fill-amber-500 text-amber-600" />
        <span className="font-medium">Trusted</span>
      </div>
    );
  }
  if (verification === 'verified') {
    return (
      <div className="flex items-center gap-1 text-xs text-secondary" title="Verified Vendor">
        <BadgeCheck className="h-3.5 w-3.5" />
        <span className="font-medium">Verified</span>
      </div>
    );
  }
  return null;
};

export default function ProductCard({ product }) {
  const vendorNeeded = product.vendor_needed === true;

  if (vendorNeeded) {
    // "Vendors Needed" card — recruits vendors instead of selling
    return (
      <Link
        to={`/register?role=vendor&category=${encodeURIComponent(product.category || '')}`}
        data-testid={`vendor-needed-card-${product.product_id}`}
      >
        <Card className="card-hover overflow-hidden border-border bg-card group relative">
          <div className="aspect-square overflow-hidden bg-muted relative">
            <img
              src={product.images?.[0] || 'https://images.unsplash.com/photo-1655147999706-91d0f00e7754?w=400'}
              alt={product.name}
              className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity"
            />
            {/* Diagonal banner */}
            <div className="absolute top-3 -right-9 rotate-45 bg-primary text-primary-foreground text-[10px] font-bold tracking-wider py-1 px-12 shadow-md">
              VENDORS NEEDED
            </div>
            {/* CTA overlay */}
            <div className="absolute inset-0 flex items-end justify-center pb-4 pointer-events-none">
              <div className="bg-primary/95 text-primary-foreground rounded-full px-4 py-2 text-xs font-semibold shadow-lg flex items-center gap-1.5">
                <Store className="h-3.5 w-3.5" />
                Become a Vendor
              </div>
            </div>
          </div>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <Badge variant="secondary" className="text-xs font-normal bg-muted text-muted-foreground">
                {product.category}
              </Badge>
            </div>
            <h3 className="font-heading font-medium text-foreground line-clamp-2 mb-1">
              {product.name}
            </h3>
            <p className="text-sm text-primary font-medium mb-1">
              Be the first vendor in this category
            </p>
            <p className="text-xs text-muted-foreground">
              Join free · Keep 90% · Reach 195 countries
            </p>
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/products/${product.product_id}`} data-testid={`product-card-${product.product_id}`}>
      <Card className="card-hover overflow-hidden border-border bg-card group">
        <div className="aspect-square overflow-hidden bg-muted relative">
          <img 
            src={product.images?.[0] || 'https://images.unsplash.com/photo-1655147999706-91d0f00e7754?w=400'}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {(product.vendor_verification === 'verified' || product.vendor_verification === 'trusted') && (
            <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1">
              <VerificationBadge verification={product.vendor_verification} />
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <Badge variant="secondary" className="text-xs font-normal bg-muted text-muted-foreground">
              {product.category}
            </Badge>
            {product.rating > 0 && (
              <div className="flex items-center gap-1 text-sm">
                <Star className="h-4 w-4 fill-primary text-primary" />
                <span className="font-medium">{product.rating}</span>
              </div>
            )}
          </div>
          <h3 className="font-heading font-medium text-foreground line-clamp-2 mb-1">
            {product.name}
          </h3>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-sm text-muted-foreground">
              by {product.vendor_name}
            </p>
            {!['verified', 'trusted'].includes(product.vendor_verification) && product.vendor_verification && (
              <VerificationBadge verification={product.vendor_verification} />
            )}
          </div>
          {product.pricing_type === 'market' ? (
            <div data-testid="market-pricing">
              <p className="font-heading font-semibold text-base text-primary leading-tight">
                Prevailing Market Price
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Commission: {product.commission_text || `${product.commission_pct ?? 0}%`} · Quote on request
              </p>
            </div>
          ) : (
            <p className="font-heading font-semibold text-lg text-primary">
              ${product.price.toFixed(2)}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
